import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Agent, OrgNode, RelationType, WorkspaceOrg } from '@orqforge/shared';
import * as d3 from 'd3';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';

interface TreeNode {
  id: string;
  name: string;
  status: string;
  agentRole: OrgNode['agentRole'];
  tier: number;
  incomingRelation?: RelationType;
  reviewers: OrgNode['reviewers'];
  children: TreeNode[];
}

const RELATIONS: RelationType[] = ['orchestrates', 'reviews', 'reports_to'];

function roleBadge(role: OrgNode['agentRole']): 'default' | 'secondary' | 'outline' {
  if (role === 'orchestrator') return 'default';
  if (role === 'reviewer') return 'secondary';
  return 'outline';
}

function buildSubTree(
  agent: OrgNode,
  allAgents: Map<string, OrgNode>,
  visited: Set<string>,
  incomingRelation?: RelationType,
): TreeNode | null {
  if (visited.has(agent.id)) return null;
  visited.add(agent.id);
  const node: TreeNode = {
    id: agent.id,
    name: agent.name,
    status: agent.status,
    agentRole: agent.agentRole,
    tier: agent.tier,
    incomingRelation,
    reviewers: agent.reviewers,
    children: [],
  };
  for (const rel of agent.relations) {
    const childAgent = allAgents.get(rel.childAgentId);
    if (!childAgent) continue;
    const childNode = buildSubTree(childAgent, allAgents, visited, rel.relationType);
    if (childNode) node.children.push(childNode);
  }
  return node;
}

function buildTree(data: WorkspaceOrg): TreeNode | null {
  const allAgents = new Map<string, OrgNode>();
  const childIds = new Set<string>();
  for (const tier of data.tiers) {
    for (const agent of tier.agents) {
      allAgents.set(agent.id, agent);
      for (const rel of agent.relations) childIds.add(rel.childAgentId);
    }
  }
  const roots = [...allAgents.values()].filter((a) => !childIds.has(a.id));
  const rootCandidates = roots.length > 0 ? roots : [...allAgents.values()];
  const virtualRoot: TreeNode = {
    id: '__root__',
    name: '',
    status: '',
    agentRole: 'orchestrator',
    tier: -1,
    reviewers: [],
    children: [],
  };
  const visited = new Set<string>();
  for (const root of rootCandidates) {
    const child = buildSubTree(root, allAgents, visited);
    if (child) virtualRoot.children.push(child);
  }
  return virtualRoot.children.length > 0 ? virtualRoot : null;
}

function roleColor(role: OrgNode['agentRole']): string {
  if (role === 'orchestrator') return 'oklch(0.68 0.14 42)';
  if (role === 'reviewer') return 'oklch(0.62 0.06 145)';
  return 'oklch(0.8 0.14 82)';
}

function nodeStroke(node: TreeNode): string {
  if (node.status !== 'active') return 'oklch(0.4 0.03 78)';
  if (node.agentRole === 'orchestrator') return 'oklch(0.68 0.14 42)';
  if (node.agentRole === 'reviewer') return 'oklch(0.62 0.06 145)';
  return 'oklch(0.8 0.14 82)';
}

function edgeColor(relation?: RelationType): string {
  if (relation === 'orchestrates') return 'oklch(0.68 0.14 42)';
  if (relation === 'reviews') return 'oklch(0.62 0.06 145)';
  if (relation === 'reports_to') return 'oklch(0.6 0.02 85)';
  return 'oklch(0.5 0.01 55)';
}

function roleIcon(role: OrgNode['agentRole']): string {
  if (role === 'orchestrator') return '◆';
  if (role === 'reviewer') return '⬡';
  return '●';
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
}

export function OrgPage() {
  const { workspaces, workspaceId } = useApp();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const viewRef = useRef<{
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    zoom: d3.ZoomBehavior<SVGSVGElement, unknown>;
  } | null>(null);
  const [org, setOrg] = useState<WorkspaceOrg | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [layoutMode, setLayoutMode] = useState<'horizontal' | 'vertical'>('vertical');
  const [tooltip, setTooltip] = useState<{ node: TreeNode; x: number; y: number } | null>(null);
  const [parentId, setParentId] = useState('');
  const [childId, setChildId] = useState('');
  const [relationType, setRelationType] = useState<RelationType>('orchestrates');

  async function load() {
    if (!workspaceId) return;
    setOrg(await apiClient.getWorkspaceOrg(workspaceId));
  }

  useEffect(() => {
    if (!workspaceId) {
      setOrg(null);
      return;
    }
    void load().catch(() => setOrg(null));
    void apiClient.listAgents().then((r) => setAgents(r.items)).catch(() => setAgents([]));
  }, [workspaceId]);

  useEffect(() => {
    const container = containerRef.current;
    const svgEl = svgRef.current;
    if (!org || !container || !svgEl) return;
    const rootData = buildTree(org);
    if (!rootData || rootData.children.length === 0) return;

    const svg = d3.select(svgEl);
    svgEl.innerHTML = '';
    const viewW = container.clientWidth || 800;
    const viewH = container.clientHeight || 500;
    svg.attr('width', viewW).attr('height', viewH);
    const zoomLayer = svg.append('g').attr('class', 'zoom-layer');
    const centerLayer = zoomLayer.append('g').attr('class', 'center-layer');
    const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.08, 5]).on('zoom', (event) => {
      zoomLayer.attr('transform', event.transform.toString());
    });
    svg.call(zoom);
    viewRef.current = { svg, zoom };

    const isHorizontal = layoutMode === 'horizontal';
    const root = d3.hierarchy<TreeNode>(rootData);
    const allNodes = root.descendants();
    let maxNameLen = 10;
    for (const d of allNodes) {
      if (d.data.id !== '__root__' && d.data.name.length > maxNameLen) maxNameLen = d.data.name.length;
    }
    const cardW = Math.max(140, Math.min(240, maxNameLen * 9 + 40));
    const cardH = 56;
    const depthCounts = new Map<number, number>();
    for (const d of allNodes) {
      if (d.data.id === '__root__') continue;
      depthCounts.set(d.depth, (depthCounts.get(d.depth) ?? 0) + 1);
    }
    const maxSiblings = Math.max(...depthCounts.values(), 1);
    const vGap = Math.max(cardH + 80, maxSiblings > 6 ? cardH + 120 : cardH + 96);
    const hGap = cardW + 200;
    d3.tree<TreeNode>()
      .nodeSize(isHorizontal ? [vGap, hGap] : [hGap, vGap])
      .separation((a, b) => (a.parent !== b.parent ? 2.5 : 1.2))(root);

    const realLinks = root.links().filter((l) => l.source.data.id !== '__root__');
    const linkPath = isHorizontal
      ? d3.linkHorizontal<d3.HierarchyPointLink<TreeNode>, d3.HierarchyPointNode<TreeNode>>()
          .x((d) => d.y)
          .y((d) => d.x)
      : d3.linkVertical<d3.HierarchyPointLink<TreeNode>, d3.HierarchyPointNode<TreeNode>>()
          .x((d) => d.x)
          .y((d) => d.y);
    const gLinks = centerLayer.append('g');
    gLinks
      .selectAll('path')
      .data(realLinks)
      .join('path')
      .attr('d', (d) => linkPath(d as d3.HierarchyPointLink<TreeNode>) ?? '')
      .attr('fill', 'none')
      .attr('stroke', (d) => edgeColor(d.target.data.incomingRelation))
      .attr('stroke-width', 2.5)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-dasharray', (d) => (d.target.data.incomingRelation === 'reports_to' ? '6,3' : 'none'));
    gLinks
      .selectAll('text')
      .data(realLinks)
      .join('text')
      .attr('x', (d) => {
        const sx = d.source.x ?? 0;
        const sy = d.source.y ?? 0;
        const tx = d.target.x ?? 0;
        const ty = d.target.y ?? 0;
        return isHorizontal ? (sy + ty) / 2 : (sx + tx) / 2;
      })
      .attr('y', (d) => {
        const sx = d.source.x ?? 0;
        const sy = d.source.y ?? 0;
        const tx = d.target.x ?? 0;
        const ty = d.target.y ?? 0;
        return isHorizontal ? (sx + tx) / 2 - 8 : (sy + ty) / 2 - 8;
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', 'oklch(0.6 0.01 55)')
      .text((d) => d.target.data.incomingRelation || '');

    const halfW = cardW / 2;
    const halfH = cardH / 2;
    const nodes = centerLayer
      .append('g')
      .selectAll('g')
      .data(root.descendants().filter((d) => d.data.id !== '__root__'))
      .join('g')
      .attr('transform', (d) => (isHorizontal ? `translate(${d.y},${d.x})` : `translate(${d.x},${d.y})`))
      .attr('cursor', 'pointer')
      .on('click', (_event, d) => navigate(`/agents/${d.data.id}`))
      .on('mouseenter', (event, d) => {
        const rect = container.getBoundingClientRect();
        setTooltip({ node: d.data, x: event.clientX - rect.left + 16, y: event.clientY - rect.top - 10 });
      })
      .on('mouseleave', () => setTooltip(null));

    nodes.append('rect')
      .attr('x', -halfW).attr('y', -halfH).attr('width', cardW).attr('height', cardH)
      .attr('rx', 10).attr('ry', 10)
      .attr('fill', 'oklch(0.24 0.014 55)')
      .attr('stroke', (d) => nodeStroke(d.data))
      .attr('stroke-width', (d) => (d.depth === 1 ? 2.5 : 2));
    nodes.append('circle')
      .attr('cx', -halfW + 14).attr('cy', -halfH + 10).attr('r', 4)
      .attr('fill', (d) => (d.data.status === 'active' ? 'oklch(0.72 0.14 150)' : 'oklch(0.82 0.15 78)'));
    nodes.append('text')
      .attr('x', -halfW + 24).attr('y', -halfH + 14)
      .attr('font-size', (d) => (d.depth === 1 ? '14px' : '12px'))
      .attr('fill', (d) => (d.depth === 1 ? 'oklch(0.8 0.14 82)' : roleColor(d.data.agentRole)))
      .text((d) => (d.depth === 1 ? '◆' : roleIcon(d.data.agentRole)));
    nodes.append('text')
      .attr('x', 0).attr('y', -6).attr('text-anchor', 'middle')
      .attr('font-size', (d) => (d.depth === 1 ? '13px' : '12px'))
      .attr('font-weight', (d) => (d.depth === 1 ? '700' : '600'))
      .attr('fill', 'oklch(0.92 0.012 55)')
      .text((d) => truncate(d.data.name, Math.floor((cardW - 50) / 7)));
    nodes.append('text')
      .attr('x', 0).attr('y', 10).attr('text-anchor', 'middle')
      .attr('font-size', '9px').attr('fill', 'oklch(0.6 0.01 55)')
      .text((d) => d.data.agentRole);
    nodes.append('text')
      .attr('x', 0).attr('y', 22).attr('text-anchor', 'middle')
      .attr('font-size', '8px').attr('fill', 'oklch(0.5 0.01 55)')
      .text((d) => `Tier ${d.data.tier}`);

    const fitTimer = window.setTimeout(() => {
      const centerEl = centerLayer.node();
      if (!centerEl) return;
      const bbox = centerEl.getBBox();
      if (bbox.width === 0 || bbox.height === 0) return;
      const padding = 60;
      const scale = Math.max(0.35, Math.min((viewW - padding * 2) / bbox.width, (viewH - padding * 2) / bbox.height, 1));
      const tx = (viewW - bbox.width * scale) / 2 - bbox.x * scale;
      const ty = (viewH - bbox.height * scale) / 2 - bbox.y * scale;
      svg.transition().duration(400).call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
    }, 50);

    const observer = new ResizeObserver(() => {
      svg.attr('width', container.clientWidth).attr('height', container.clientHeight);
    });
    observer.observe(container);
    return () => {
      window.clearTimeout(fitTimer);
      observer.disconnect();
      viewRef.current = null;
    };
  }, [org, layoutMode, navigate]);

  function zoomBy(factor: number) {
    const view = viewRef.current;
    if (!view) return;
    view.svg.transition().duration(300).call(view.zoom.scaleBy, factor);
  }

  function zoomFit() {
    const view = viewRef.current;
    const svgEl = svgRef.current;
    if (!view || !svgEl) return;
    const centerGroup = svgEl.querySelector('.center-layer') as SVGGraphicsElement | null;
    if (!centerGroup) return;
    const bounds = centerGroup.getBBox();
    if (!bounds.width || !bounds.height) return;
    const viewW = svgEl.clientWidth;
    const viewH = svgEl.clientHeight;
    const padding = 40;
    const scale = Math.max(0.35, Math.min((viewW - padding * 2) / bounds.width, (viewH - padding * 2) / bounds.height, 1));
    const tx = (viewW - bounds.width * scale) / 2 - bounds.x * scale;
    const ty = (viewH - bounds.height * scale) / 2 - bounds.y * scale;
    view.svg.transition().duration(400).call(view.zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
  }

  async function addRelation() {
    if (!parentId || !childId || parentId === childId) return;
    await apiClient.addRelation(parentId, childId, relationType);
    setChildId('');
    await load();
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Org & Hierarchy</h1>
          <p className="text-sm text-gray-500">
            Agent hierarchy tree for <strong>{workspace?.name || 'no workspace'}</strong>.
            <span className="hidden sm:inline"> Drag to pan. Scroll to zoom. Click a node for details.</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" title="Zoom in" onClick={() => zoomBy(1.3)}>+</Button>
          <Button size="sm" variant="ghost" title="Zoom out" onClick={() => zoomBy(0.7)}>-</Button>
          <Button size="sm" variant="ghost" title="Fit to screen" onClick={zoomFit}>Fit</Button>
          <Button
            size="sm"
            variant="ghost"
            className={layoutMode === 'vertical' ? 'text-blue-600' : ''}
            title="Toggle layout direction"
            onClick={() => setLayoutMode((m) => (m === 'horizontal' ? 'vertical' : 'horizontal'))}
          >
            {layoutMode === 'vertical' ? '→' : '↓'}
          </Button>
        </div>
      </div>

      {!workspaceId ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          Select a workspace from the sidebar to view its org.
        </div>
      ) : org && (
        <>
          {org.tiers.length === 0 ? (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
              No agents in this workspace yet.
            </div>
          ) : (
            <>
              <div className="relative mt-4 h-[70vh] min-h-[500px] w-full cursor-grab overflow-hidden rounded-lg border border-gray-200 bg-gray-50 active:cursor-grabbing dark:border-gray-800 dark:bg-gray-900" ref={containerRef}>
                <svg ref={svgRef} className="h-full w-full" />
                {tooltip && (
                  <div
                    className="pointer-events-none absolute z-50 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                    style={{ left: tooltip.x, top: tooltip.y }}
                  >
                    <div className="mb-1 text-sm font-semibold">{tooltip.node.name}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Badge variant={roleBadge(tooltip.node.agentRole)}>{tooltip.node.agentRole}</Badge>
                      <Badge variant={tooltip.node.status === 'active' ? 'success' : 'warning'}>{tooltip.node.status}</Badge>
                      <span>Tier {tooltip.node.tier}</span>
                    </div>
                    {tooltip.node.reviewers.length > 0 && (
                      <div className="mt-1.5 border-t border-gray-200 pt-1.5 dark:border-gray-700">
                        <div className="mb-0.5 text-[0.65rem] uppercase tracking-wide text-gray-400">Reviewers</div>
                        {tooltip.node.reviewers.map((rv) => (
                          <div key={rv.userId} className="text-xs text-gray-600 dark:text-gray-300">
                            {rv.displayName || rv.userId.slice(0, 8)} · {rv.role}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-600" /> Orchestrator</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-500" /> Reviewer</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" /> Specialist</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-green-600" /> Active</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" /> Paused</span>
              </div>
            </>
          )}

          <div className="mt-6 max-w-2xl rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h2 className="text-sm font-semibold">Add relationship</h2>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="flex-1 text-xs text-gray-500">
                Parent agent
                <select className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                  <option value="">Select</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="w-full text-xs text-gray-500 sm:w-40">
                Relationship
                <select className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" value={relationType} onChange={(e) => setRelationType(e.target.value as RelationType)}>
                  {RELATIONS.map((r) => <option key={r} value={r}>{r === 'reports_to' ? 'reports to' : r}</option>)}
                </select>
              </label>
              <label className="flex-1 text-xs text-gray-500">
                Child agent
                <select className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" value={childId} onChange={(e) => setChildId(e.target.value)}>
                  <option value="">Select</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <Button size="sm" disabled={!parentId || !childId || parentId === childId} onClick={() => void addRelation()}>
                Link
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
